import { createEvent, sample } from "effector"
import { createPersistStore } from "@callcenter/effector-helpers"
import { is } from "@callcenter/helpers"
import { $userService } from "@callcenter/effector-services"

import * as selfServiceModel from "./self-service.model"
import { selfServiceApi } from "../api"

type OnboardingInfo = {
  isFirstDayShown: boolean
  isLastDayShown: boolean
  userId: null | number
}

export const [$onboardingInfo, onboardingInfoChanged] = createPersistStore<OnboardingInfo>("ss_onboarding", {
  initialValue: {
    isFirstDayShown: false,
    isLastDayShown: false,
    userId: null
  },
  contract: (raw): raw is OnboardingInfo => {
    if (!is.object(raw) || !("userId" in raw)) {
      return false
    }
    // Default state (userId === null) is the store's initial value, not stored data — accept it
    // so persistence doesn't reject it before the user store is hydrated.
    if (raw.userId === null) {
      return true
    }

    // eslint-disable-next-line effector/no-getState
    return raw.userId === $userService.$userSettings.getState().id
  }
})

// Should connect with webchat
export const handleSetup = createEvent()
export const handleUpgrade = createEvent()
export const handleClose = createEvent()
export const handleCloseOutside = createEvent()

sample({
  clock: handleClose,
  source: {
    onboarding: $onboardingInfo,
    user: $userService.$userSettings,
    trialDaysRemaining: selfServiceModel.$trialDaysRemaining
  },
  filter: ({ onboarding: { isFirstDayShown, isLastDayShown } }) => !isFirstDayShown || !isLastDayShown,
  fn: ({ onboarding, user, trialDaysRemaining }) => ({
    ...onboarding,
    userId: user.id,
    isFirstDayShown: true,
    isLastDayShown: trialDaysRemaining <= 1 ? true : onboarding.isLastDayShown
  }),
  target: onboardingInfoChanged
})

sample({
  clock: [handleSetup, handleUpgrade],
  target: handleClose
})

sample({
  clock: handleUpgrade,
  target: selfServiceModel.upgradeNowClicked
})

sample({
  clock: handleSetup,
  source: selfServiceModel.$setupSettings,
  filter: ({ isSelfServiceMode }) => isSelfServiceMode,
  fn: ({ webchatTrialSettings }) => ({
    ...webchatTrialSettings,
    shouldOpenTrialHome: true
  }),
  target: selfServiceApi.startSetupFx
})

sample({
  clock: handleCloseOutside,
  source: selfServiceModel.$setupSettings,
  filter: ({ isSelfServiceMode }) => isSelfServiceMode,
  fn: ({ webchatTrialSettings }) => ({
    ...webchatTrialSettings,
    shouldOpenTrialPreview: true
  }),
  target: selfServiceApi.startSetupFx
})
